import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Box,
  Button,
  Heading,
  Input,
  Stack,
  useToast,
  Avatar,
  Text,
  Progress,
  Flex,
  useColorModeValue,
} from "@chakra-ui/react";
import Select from "react-select";
import axios from "axios";
import { setToken, setRefreshToken, setUserType } from "../utils/authService";
import { useNavigate } from "react-router-dom";



const steps = [
  { id: 1, label: "Visual Creator?", description: "Are you a photographer or customer?" },
  { id: 2, label: "Basic Information", description: "Enter your name and number" },
  { id: 3, label: "Location", description: "Where can clients find you?" },
  { id: 4, label: "Login Details", description: "Create your username and password" },
  { id: 5, label: "Profile Setup", description: "Upload your profile picture" },
];

const options = [
  { value: "Street", label: "Street" },
  { value: "Boudoir", label: "Boudoir" },
  { value: "Weddings", label: "Weddings" },
  { value: "Events", label: "Events" },
  { value: "Editorial", label: "Editorial" },
  { value: "Last Minute", label: "Last Minute" },
  { value: "Social Media", label: "Social Media" },
  { value: "Real Estate", label: "Real Estate" },
];

const OnboardingModal = ({ isOpen, onClose, setIsLoggedIn }) => {
  const bgColor = useColorModeValue("white", "black");
  const placeholderColor = "gray.500";
  const toast = useToast();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    number: "",
    username: "",
    password: "",
    user_type: "",
    street_address: "",
    city: "",
    state: "",
    zip: "",
    country: "US",
    camera: "",
    equipment: "",
    photography_style: "",
    profilePicture: null,
    fileName: "",
    fileType: "",
  });

  const isNextDisabled = () => {
    switch (currentStep) {
      case 0:
        // ✅ Allow navigating to /onboarding immediately if photographer selected
        if (formData.user_type !== "customer") return false;
  
        return !formData.user_type;
      case 1:
        return !formData.first_name.trim() || !formData.last_name.trim() || !formData.number.trim();
  
  
      case 2:
        return !formData.street_address?.trim() || !formData.city?.trim() || !formData.state?.trim() || !formData.zip?.trim();
  
      case 3:
        return !formData.email.trim() || !formData.password.trim();
  
      case 4:
        return !formData.profilePicture;
  
      default:
        return false;
    }
  };
  

  const handleNext = () => {
    if (currentStep === 0 && formData.user_type !== "customer") {
      onClose(); // Close this modal
      toast({
        title: "Let's get you set up as a visual creator!",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      navigate("/onboarding"); // Navigate to full onboarding page
      return; // Stop here
    }
  
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async () => {
    try {
      const fullAddress = `${formData.street_address}, ${formData.city}, ${formData.state}, ${formData.zip}, ${formData.country}`;

      let latitude = '';
      let longitude = '';

      try {
        const google_response = await axios.get(`https://maps.googleapis.com/maps/api/geocode/json`, {
          params: {
            address: fullAddress,
            key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY
          }
        });

        const location = google_response?.data?.results?.[0]?.geometry?.location;
        if (location) {
          latitude = location.lat;
          longitude = location.lng;
        }
      } catch (geoError) {
        console.warn("Could not retrieve geolocation. Proceeding without lat/lng.");
      }
      
      const updatedFormData = {
        ...formData,
        latitude,
        longitude,
        user_type: 'customer', // ✅ enforce this if not already set
      };

      const backendUrl =
        process.env.NODE_ENV === "production" ? process.env.REACT_APP_BACKEND_URL : "http://localhost:8080";

      const response = await axios.post(`${backendUrl}/signup`, updatedFormData, { headers: { "Content-Type": "application/json" } });
      console.log("CHECKING", response)
      
      const { token, refresh_token, user_type } = response.data.user;
      setToken(token);
      setRefreshToken(refresh_token);
      setUserType(user_type);
      
      setIsLoggedIn(true);
      toast({ title: "Signup successful!", status: "success", duration: 3000, isClosable: true });
      onClose();
    } catch (error) {
      console.error("Error during signup:", error);
      toast({ title: "Signup failed.", description: "Please try again.", status: "error", duration: 3000, isClosable: true });
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <Stack spacing={6} align="center">
            <Stack direction="row" justify="center" spacing={6}>
              <Button variant={formData.user_type === "photographer" ? "solid" : "outline"} onClick={() => setFormData({ ...formData, user_type: "photographer" })}>
                Visual Creator
              </Button>
              <Button variant={formData.user_type === "customer" ? "solid" : "outline"} onClick={() => setFormData({ ...formData, user_type: "customer" })}>
                Customer
              </Button>
            </Stack>
          </Stack>
        );
      case 1:
        return (
          <Stack spacing={4}>
            <Input placeholder="First Name" name="first_name" value={formData.first_name} onChange={handleChange} _placeholder={{ color: placeholderColor }} />
            <Input placeholder="Last Name" name="last_name" value={formData.last_name} onChange={handleChange} _placeholder={{ color: placeholderColor }} />
            <Input placeholder="Your Number" name="number" type="number" value={formData.number} onChange={handleChange} _placeholder={{ color: placeholderColor }} />
          </Stack>
        );
      case 2:
        return (
          <Stack spacing={4}>
            <Input placeholder="Street Address" name="street_address" value={formData.street_address} onChange={handleChange} _placeholder={{ color: placeholderColor }} />
            <Input placeholder="City" name="city" value={formData.city} onChange={handleChange} _placeholder={{ color: placeholderColor }} />
            <Input placeholder="State" name="state" value={formData.state} onChange={handleChange} _placeholder={{ color: placeholderColor }} />
            <Input placeholder="ZIP Code" name="zip" value={formData.zip} onChange={handleChange} _placeholder={{ color: placeholderColor }} />
          </Stack>
        );
      case 3:
        return (
          <Stack spacing={4}>
            <Input placeholder="Your Email" name="email" type="email" value={formData.email} onChange={handleChange} _placeholder={{ color: placeholderColor }} />
            <Input placeholder="Password" name="password" type="password" value={formData.password} onChange={handleChange} _placeholder={{ color: placeholderColor }} />
          </Stack>
        );
      case 4:
        return (
          <Stack spacing={4} align="center">
            {formData.profilePicture && (
              <Avatar size="xl" src={URL.createObjectURL(formData.profilePicture)} />
            )}
            <Input
              type="file"
              name="profilePicture"
              accept="image/jpeg, image/png, image/heic, image/heif"
              onChange={(e) =>
                setFormData({
                  ...formData,
                  profilePicture: e.target.files[0],
                  fileName: e.target.files[0]?.name || '',
                  fileType: e.target.files[0]?.type || '',
                })
              }
            />
          </Stack>
        );
      default:
        return null;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent p={4} bg={bgColor} borderRadius="xl">
        <ModalHeader>{steps[currentStep].label}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>{renderStep()}</ModalBody>
        <ModalFooter>
          <Button onClick={handleBack} isDisabled={currentStep === 0}>Back</Button>
          <Button onClick={handleNext} isDisabled={isNextDisabled()}>{currentStep === steps.length - 1 ? "Finish" : "Next"}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default OnboardingModal;
